import { clsx } from 'clsx';
import styles from './StatBar.module.css';

export interface Stat {
  /**
   * The main value to display (e.g., "99.2%", "10k+")
   */
  value: string;

  /**
   * Label describing the stat (e.g., "Accuracy", "Documents")
   */
  label: string;
}

export interface StatBarProps {
  /**
   * Array of statistics to display
   */
  stats: Stat[];

  /**
   * Visual style variant
   * @default 'default'
   */
  variant?: 'default' | 'simple' | 'solid';

  /**
   * Show dividers between stats
   * @default false
   */
  divider?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * StatBar Component
 *
 * Displays a row of statistics, typically used in hero sections
 * to showcase key metrics or achievements.
 *
 * @example
 * ```tsx
 * <StatBar
 *   stats={[
 *     { value: "99.2%", label: "Accuracy" },
 *     { value: "2.8s", label: "Avg. Processing Time" },
 *     { value: "10k+", label: "Documents / Month" }
 *   ]}
 *   divider
 * />
 * ```
 */
export const StatBar = ({
  stats,
  variant = 'default',
  divider = false,
  className
}: StatBarProps) => {
  return (
    <div
      className={clsx(
        styles.statBar,
        styles[`variant-${variant}`],
        divider && styles.divider,
        className
      )}
      role="list"
      aria-label="Statistics"
    >
      {stats.map((stat, index) => (
        <div key={`${stat.label}-${index}`} className={styles.stat} role="listitem">
          <div className={styles.value} aria-label={`${stat.value} ${stat.label}`}>
            {stat.value}
          </div>
          <div className={styles.label}>{stat.label}</div>
        </div>
      ))}
    </div>
  );
};
